/** tpl → argument-editor dispatch. Templates absent from this map either take
 *  no args (`NO_ARGS_TPLS`) or are deprecated/unknown (read-only raw view). */
import { Box } from '@mui/material';
import type { ComponentType } from 'react';

import { TPL } from '../ruleCatalog';
import type { RuleDraft } from '../ruleTypes';
import { AffectedFieldsArgs } from './AffectedFieldsArgs';
import type { ArgEditorProps } from './common';
import { FieldExistsArgs } from './FieldExistsArgs';
import {
  CountryArgs,
  DeliveryMethodArgs,
  EventTypeArgs,
  LanguageArgs,
  NestedRulesetArgs,
  OtaArgs,
  OtherToursArgs,
  TagArgs,
  TagCategoryArgs,
} from './LookupArgs';
import {
  CheckinSourceArgs,
  DeltaArgs,
  EmailReceiptArgs,
  LocationRadiusArgs,
  LoggedInArgs,
  PrecheckinStatusArgs,
  RebookingArgs,
  ReviewPosNegArgs,
  ReviewRatingArgs,
  SettlementMethodArgs,
  SubbookingsScopeArgs,
  SurveyStateArgs,
  UpdateSourcesArgs,
} from './SimpleArgs';
import { DtRangeArgs, TripTimeRangeArgs } from './TimeArgs';

export type { ArgEditorProps } from './common';

export const ARG_EDITORS: Record<number, ComponentType<ArgEditorProps>> = {
  [TPL.USER_LOGGED_IN]: LoggedInArgs,
  [TPL.USER_COUNTRY]: CountryArgs,
  [TPL.PHONE_COUNTRY]: CountryArgs,
  [TPL.USER_LANG]: LanguageArgs,
  [TPL.USER_OTA]: OtaArgs,
  [TPL.IS_FIELD_AFFECTED]: AffectedFieldsArgs,
  [TPL.IS_UPDATED_VIA]: UpdateSourcesArgs,
  [TPL.USER_HAS_TAG_CATEGORY]: TagCategoryArgs,
  [TPL.TOUR_HAS_TAG_CATEGORY]: TagCategoryArgs,
  [TPL.OTA_HAS_TAG_CATEGORY]: TagCategoryArgs,
  [TPL.TOUR_OPERATOR_HAS_TAG_CATEGORY]: TagCategoryArgs,
  [TPL.ORDER_HAS_TAG_CATEGORY]: TagCategoryArgs,
  [TPL.ACCOUNT_HAS_TAG_CATEGORY]: TagCategoryArgs,
  [TPL.USER_HAS_TAG]: TagArgs,
  [TPL.TOUR_HAS_TAG]: TagArgs,
  [TPL.OTA_HAS_TAG]: TagArgs,
  [TPL.TOUR_OPERATOR_HAS_TAG]: TagArgs,
  [TPL.ORDER_HAS_TAG]: TagArgs,
  [TPL.ACCOUNT_HAS_TAG]: TagArgs,
  [TPL.SURVEY_STATE]: SurveyStateArgs,
  [TPL.IS_REBOOKING]: RebookingArgs,
  [TPL.IS_FIELD_PRESENT]: FieldExistsArgs,
  [TPL.TRIP_BEGINS_WITHIN_X_TIME]: DeltaArgs,
  [TPL.BOOKING_DATETIME_RANGE]: DeltaArgs,
  [TPL.REVIEW_IS_POSITIVE_NEGATIVE]: ReviewPosNegArgs,
  [TPL.USER_IS_NEAR_LOCATION]: LocationRadiusArgs,
  [TPL.REVIEW_RATING]: ReviewRatingArgs,
  [TPL.HAS_OTHER_TOURS]: OtherToursArgs,
  [TPL.RULESET]: NestedRulesetArgs,
  [TPL.EVENT_TYPE]: EventTypeArgs,
  [TPL.DELIVERY_METHOD]: DeliveryMethodArgs,
  [TPL.CHECKIN_SOURCE]: CheckinSourceArgs,
  [TPL.TRIP_TIME_RANGE]: TripTimeRangeArgs,
  [TPL.PRECHECKIN_STATUS]: PrecheckinStatusArgs,
  [TPL.EMAIL_RECEIPT]: EmailReceiptArgs,
  [TPL.SETTLEMENT_METHOD]: SettlementMethodArgs,
  [TPL.HAS_SUB_BOOKINGS]: SubbookingsScopeArgs,
  [TPL.DATETIME_WITHIN_RANGE]: DtRangeArgs,
  [TPL.DATE_WITHIN_RANGE]: DtRangeArgs,
  [TPL.TIME_WITHIN_RANGE]: DtRangeArgs,
};

/** Read-only pretty-printed args for deprecated/unknown templates. */
export function RawArgsView({ rule }: { rule: RuleDraft }) {
  if (rule.args == null) return null;
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        fontFamily: 'monospace',
        fontSize: 12,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflowX: 'auto',
      }}
    >
      {JSON.stringify(rule.args, null, 2)}
    </Box>
  );
}
